import { resolveControlledCode } from "../../../utils/vocabulary";

function normalizeLocalizedEntries(value, baseLang) {
  if (Array.isArray(value)) {
    return value
      .map((entry) => {
        if (typeof entry === "string") {
          return { language: baseLang, value: entry };
        }

        if (!entry || typeof entry !== "object") return null;

        return {
          language: entry.language ?? entry.lang ?? baseLang,
          value: entry.value ?? entry.text ?? "",
        };
      })
      .filter(
        (entry) =>
          entry &&
          typeof entry.language === "string" &&
          entry.language.trim() !== "",
      );
  }

  if (value && typeof value === "object") {
    return [
      {
        language: value.language ?? value.lang ?? baseLang,
        value: value.value ?? value.text ?? "",
      },
    ];
  }

  if (typeof value === "string" && value.trim() !== "") {
    return [{ language: baseLang, value }];
  }

  return [{ language: baseLang, value: "" }];
}

function firstValue(value) {
  if (Array.isArray(value)) return value[0] ?? "";
  return value ?? "";
}

function normalizeStringArray(value) {
  if (Array.isArray(value)) {
    return value.filter(
      (entry) => typeof entry === "string" && entry.trim() !== "",
    );
  }

  if (typeof value === "string" && value.trim() !== "") {
    return [value];
  }

  return [];
}

export function createInitialParticipantFormData(baseLang = "en") {
  return {
    participant_id: crypto.randomUUID(),
    participant_name: [{ language: baseLang, value: "" }],
    externalIdentifier: "",
    participant_identifier: "",
    status: "ACTIVE",
    country: "",
    adminUnit2: "",
    activity_sector: "",
    address: "",
    postCode: "",
    postName: "",
    contact_point: "",
    telephone: "",
    openingHours: "",
    description: [{ language: baseLang, value: "" }],
    purpose: [],
    connector_status: "ACTIVE",
    registration_date: new Date().toISOString().split("T")[0],
  };
}

export function normalizeParticipantFormData(formData, baseLang = "en") {
  const initial = createInitialParticipantFormData(baseLang);

  if (!formData) return initial;

  return {
    ...initial,
    ...formData,
    participant_name: normalizeLocalizedEntries(
      formData.participant_name,
      baseLang,
    ),
    description: normalizeLocalizedEntries(formData.description, baseLang),
    purpose: normalizeStringArray(formData.purpose),
  };
}

export function mapParticipantToFormData(participant, baseLang = "en") {
  const initial = createInitialParticipantFormData(baseLang);

  if (!participant) return initial;

  return {
    ...initial,
    participant_id:
      participant?.connector?.[0]?.identifier ??
      participant?.dsbParticipantId ??
      participant?.participant_id ??
      initial.participant_id,
    participant_name: normalizeLocalizedEntries(
      participant?.identifier?.[0]?.schemeName || participant?.preferredLabel,
      baseLang,
    ),
    externalIdentifier:
      participant?.externalIdentifier ??
      participant?.identifier?.[0]?.notation ??
      "",
    participant_identifier:
      participant?.externalIdentifier ??
      participant?.identifier?.[0]?.notation ??
      "",
    status: participant?.status ?? initial.status,
    country: participant?.address?.adminUnit1 ?? "",
    adminUnit2: participant?.address?.adminUnit2 ?? "",
    activity_sector: firstValue(participant?.classification),
    address: participant?.address?.fullAddress ?? "",
    postCode: participant?.address?.postCode ?? "",
    postName: participant?.address?.postName ?? "",
    contact_point:
      participant?.contactPoint?.email ??
      participant?.contact_point?.email ??
      "",
    telephone:
      participant?.contactPoint?.telephone ??
      participant?.contact_point?.telephone ??
      "",
    openingHours:
      participant?.contactPoint?.openingHours ??
      participant?.contact_point?.openingHours ??
      "",
    description: normalizeLocalizedEntries(participant?.description, baseLang),
    purpose: normalizeStringArray(participant?.purpose),
    connector_status:
      participant?.connector?.[0]?.status ?? initial.connector_status,
    registration_date:
      participant?.registration_date ?? initial.registration_date,
  };
}

export function buildParticipantPayload(
  formData,
  baseLang,
  { postalCodeOptions = [], provinceOptions = [] } = {},
) {
  const postalCode = resolveControlledCode(formData.postCode, postalCodeOptions);
  const adminUnit2 = resolveControlledCode(formData.adminUnit2, provinceOptions);

  const participantNameEntries = normalizeLocalizedEntries(
    formData.participant_name,
    baseLang,
  ).filter((entry) => String(entry.value ?? "").trim() !== "");
  const descriptionEntries = normalizeLocalizedEntries(
    formData.description,
    baseLang,
  ).filter((entry) => String(entry.value ?? "").trim() !== "");

  return {
    status: formData.status,
    externalIdentifier:
      formData.externalIdentifier ?? formData.participant_identifier ?? "",
    identifier: [
      {
        notation:
          formData.externalIdentifier ?? formData.participant_identifier ?? "",
        schemeName: participantNameEntries.map((entry) => ({
          language: entry.language,
          value: entry.value,
        })),
      },
    ],
    preferredLabel: participantNameEntries.map((entry) => ({
      language: entry.language,
      value: entry.value,
    })),
    description: descriptionEntries.map((entry) => ({
      language: entry.language,
      value: entry.value,
    })),
    classification: [formData.activity_sector],
    purpose: normalizeStringArray(formData.purpose),
    address: {
      adminUnit1: formData.country,
      adminUnit2,
      fullAddress: formData.address,
      postCode: postalCode,
      postName: formData.postName,
    },
    contactPoint: {
      email: formData.contact_point,
      telephone: formData.telephone,
      openingHours: formData.openingHours,
    },
    connector: [
      {
        identifier: formData.participant_id,
        status: formData.connector_status,
      },
    ],
  };
}

export function buildRegistryParticipantPayload(
  formData,
  baseLang,
  { postalCodeOptions = [], provinceOptions = [] } = {},
) {
  const postalCode = resolveControlledCode(
    formData.address?.postCode,
    postalCodeOptions,
  );
  const adminUnit2 = resolveControlledCode(
    formData.address?.adminUnit2,
    provinceOptions,
  );

  const preferredLabelEntries = normalizeLocalizedEntries(
    formData.preferredLabel,
    baseLang,
  ).filter((entry) => String(entry.value ?? "").trim() !== "");
  const descriptionEntries = normalizeLocalizedEntries(
    formData.description,
    baseLang,
  ).filter((entry) => String(entry.value ?? "").trim() !== "");

  return {
    externalIdentifier: formData.externalIdentifier,
    preferredLabel: preferredLabelEntries.map((entry) => ({
      language: entry.language,
      value: entry.value,
    })),
    description: descriptionEntries.map((entry) => ({
      language: entry.language,
      value: entry.value,
    })),
    classification: Array.isArray(formData.classification)
      ? formData.classification
      : [],
    purpose: normalizeStringArray(formData.purpose),
    roles: normalizeStringArray(formData.roles),
    address: {
      adminUnit1: formData.address?.adminUnit1 ?? "",
      adminUnit2,
      fullAddress: formData.address?.fullAddress ?? "",
      postCode: postalCode,
      postName: formData.address?.postName ?? "",
    },
    contactPoint: {
      email: formData.contactPoint?.email ?? "",
      telephone: formData.contactPoint?.telephone ?? "",
      openingHours: formData.contactPoint?.openingHours ?? "",
    },
  };
}
